
import {
  createComponent,
  reactive,
  computed,
  inject,
  onBeforeMount,
  onBeforeUnmount,
} from "@vue/composition-api";
import TypeWellButton from "./TypeWellButton.vue";
import TypeWellMissCount from "./TypeWellMissCount.vue";
import TypeWellLapTime from "./TypeWellLapTime.vue";
import ResultStoreKey from "./result-store-key";
import { eLogType } from "@/stores/result-store";
import { eReplayRomanState } from "@/lib/typeWell";

interface typingLogObj {
  /** ログの種類 */
  logType: eLogType;
  /** タイム（ミリ秒） */
  timeMs: number;
  /** その時点でのローマ字 */
  roman: string;
}

interface ReplayRomanCharObj {
  char: string;
  status: eReplayRomanState;
  key: string;
}

interface ReplayRomanLineObj {
  list: ReplayRomanCharObj[];
  key: string;
}

export default createComponent({
  components: {
    TypeWellButton,
    TypeWellMissCount,
    TypeWellLapTime,
  },
  setup() {
    const resultStore = inject(ResultStoreKey);
    if (!resultStore) {
      throw new Error(`${ResultStoreKey} is not provided`);
    }

    const state = reactive({
      typingLog: computed(() => resultStore.typingLog),
      // 現在のtypingLog
      // eventIndex が typingLog の範囲外であれば、空のオブジェクトを返す
      curLog: computed(
        (): typingLogObj => {
          if (state.typingLog.empty()) {
            return {} as typingLogObj;
          }
          return state.eventIndex < state.typingLog.length
            ? state.typingLog[state.eventIndex]
            : state.typingLog.back();
        }
      ),
      roman: computed((): string => {
        return state.curLog.roman === undefined
          ? ""
          : state.curLog.roman.slice(state.curLog.roman.length - 400);
      }),
      additionalRomanCount: computed((): number =>
        Math.max(state.curLog.roman.length - 400, 0)
      ),
      romanDataList: computed((): ReplayRomanLineObj[] => {
        let ret: ReplayRomanLineObj[] = [];
        if (state.curLog.roman === undefined) return ret;
        [].forEach.call(state.curLog.roman, (c: string, allIndex: number) => {
          const showIndex = allIndex - state.additionalRomanCount;
          if (showIndex >= 0) {
            if (showIndex % 50 === 0) {
              ret.push({ list: [], key: `romanList${showIndex / 50}` });
            }

            if (allIndex < state.romanStateList.length) {
              if (c === " ") c = "_";
              ret.back().list.push({
                char: c,
                status: state.romanStateList[allIndex],
                key: `romanChar${showIndex}`,
              });
            } else {
              ret.back().list.push({
                char: c,
                status: eReplayRomanState.Next,
                key: `romanChar${showIndex}`,
              });
            }
          }
        });
        return ret;
      }),
      lapTimeMsList: computed(() => {
        let ret: number[] = [];
        const list = resultStore.lapTimeMsList;
        for (let lapTimeMs of list) {
          if (lapTimeMs <= state.elapsedTimeMs) ret.push(lapTimeMs);
        }
        return ret;
      }),
      romanStateList: [] as eReplayRomanState[],
      eventIndex: 0,
      isRunning: false,
      startTimeMs: 0,
      elapsedTimeMs: 0,
      displayTime: computed((): string =>
        (Math.floor(state.elapsedTimeMs / 100) / 10).toFixed(1)
      ),
      missCount: 0,
    });

    function startRAFLoop() {
      state.startTimeMs = Date.now();
      window.requestAnimationFrame(loop);
    }
    function loop() {
      if (state.isRunning) {
        const nowTime = Date.now();
        state.elapsedTimeMs = nowTime - state.startTimeMs;
        window.requestAnimationFrame(loop);

        // 更新処理
        while (
          state.eventIndex < state.typingLog.length &&
          state.elapsedTimeMs >= state.typingLog[state.eventIndex].timeMs
        ) {
          switch (state.typingLog[state.eventIndex].logType) {
            // 正しい入力
            case eLogType.Correct:
              if (
                !state.romanStateList.empty() &&
                state.romanStateList.back() === eReplayRomanState.NextMiss
              ) {
                state.romanStateList.pop();
                state.romanStateList.push(eReplayRomanState.PrevMiss);
              } else {
                state.romanStateList.push(eReplayRomanState.Prev);
              }
              break;
            // ミス
            case eLogType.Miss:
              ++state.missCount;
              if (
                state.romanStateList.empty() ||
                state.romanStateList.back() !== eReplayRomanState.NextMiss
              ) {
                state.romanStateList.push(eReplayRomanState.NextMiss);
              }
              break;
            default:
              throw new Error("invalid logType");
          }
          ++state.eventIndex;
        }

        if (state.eventIndex >= state.typingLog.length) {
          state.isRunning = false;
        }
      }
    }

    function keyInput(event: KeyboardEvent) {
      if (event.key === "Escape") {
        state.isRunning = false;
      }
    }

    function startReplay() {
      state.isRunning = !state.isRunning;
      if (state.isRunning) {
        if (state.typingLog.length > 0) {
          while (!state.romanStateList.empty()) {
            state.romanStateList.pop();
          }
          state.eventIndex = 0;
          state.missCount = 0;
          startRAFLoop();
        } else {
          state.isRunning = false;
          alert("リプレイファイルがありません");
        }
      }
    }

    onBeforeMount(() => {
      window.addEventListener("keydown", keyInput, true);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("keydown", keyInput, true);
    });

    function isNextRoman(status: eReplayRomanState): boolean {
      return status === eReplayRomanState.Next;
    }
    function isPrevRoman(status: eReplayRomanState): boolean {
      return status === eReplayRomanState.Prev;
    }
    function isMissRoman(status: eReplayRomanState): boolean {
      return (
        status === eReplayRomanState.PrevMiss ||
        status === eReplayRomanState.NextMiss
      );
    }

    return {
      state,
      startReplay,
      isNextRoman,
      isPrevRoman,
      isMissRoman,
    };
  },
});
